import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { reportsApi, type ReportData } from '../../api/reports.js';
import { paymentBankApi } from '../../api/paymentBank.js';
import { paymentRecurringApi, type RecurringEditBody } from '../../api/paymentRecurring.js';
import { ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { BankSelect } from '../../components/BankSelect.js';
import { Spinner, ErrorBox, EmptyBox, Pagination, SearchInput, Field } from '../../components/ui.js';
import { useUrlSearch, useUrlUpdate } from '../../lib/hooks.js';
import { dateOnly, datetime, formatAmountDraft, isZeroDate, localDate, money, text } from '../../lib/format.js';

type Row = Record<string, unknown>;

const KIND_TABS = [
  { value: '', label: '전체내역' },
  { value: '1', label: '정기 · 계좌이체' },
  { value: '2', label: '정기 · 카드' },
  { value: '3', label: '수시 · 계좌이체' },
  { value: '4', label: '수시 · 카드' },
];

const USE_AREAS: Record<string, string> = {
  '0': '미지정', '1': '사무실', '2': '물류센터', '3': '의정부 매장',
  '4': '청담 매장', '5': 'CS팀', '6': '마케팅', '7': '디자인팀',
  '8': '기획팀', '9': '상품관리팀', '10': 'TV바나나', '11': '기타',
};

const PAY_DAYS: Record<string, string> = {
  random: '수시결제', fmonth: '매달 초', lmonth: '매달 말',
  year1: '매년 1월경', year2: '매년 2월경', year3: '매년 3월경',
  year4: '매년 4월경', year5: '매년 5월경', year6: '매년 6월경',
  year7: '매년 7월경', year8: '매년 8월경', year9: '매년 9월경',
  year10: '매년 10월경', year11: '매년 11월경', year12: '매년 12월경',
};

function payDayLabel(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '-';
  if (/^\d+$/.test(raw)) return `${Number(raw)}일`;
  return PAY_DAYS[raw] ?? raw;
}

/** 결제일 드롭다운의 선택지 — 원본 $pay_day_ary 의 키 + 숫자 1~31(그날 결제). */
const PAY_DAY_OPTIONS: { value: string; label: string }[] = [
  ...Object.entries(PAY_DAYS).map(([value, label]) => ({ value, label })),
  ...Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: `${i + 1}일` })),
];

const PAY_CYCLES: Record<string, string> = { ROU: '정기결제', RND: '수시결제' };
const PAY_TYPES: Record<string, string> = { BANK: '계좌이체', CARD: '카드결제' };
const USE_STATUSES: Record<string, string> = { '1': '사용', '2': '미사용' };

interface RecurringFormState {
  pay_name: string;
  pay_cycle: string;
  pay_day: string;
  pay_type: string;
  amountDraft: string;
  amountUsDraft: string;
  bankcode: string;
  banksn: string;
  bankinname: string;
  cardcom: string;
  use_area: string;
  up_name: string;
  use_status: string;
  last_pay_day: string;
  memo: string;
}

/** 금액 컬럼(정수)을 입력칸 초깃값("220,000")으로. 빈 값·0 은 빈 칸으로 둔다. */
function amountDraftOf(value: unknown): string {
  const n = Number(value ?? 0);
  return Number.isFinite(n) && n > 0 ? n.toLocaleString() : '';
}

/**
 * 수정은 입금건·카드건 수정처럼 그 건의 값을 그대로 베껴 채운다. 등록은 빈 폼에서
 * 시작하고 담당자만 로그인한 계정 이름을 채운다(입금건의 등록자와 같은 규칙).
 */
function toRecurringFormState(
  row: Row | null,
  defaultUpName: string,
  mode: 'create' | 'edit',
): RecurringFormState {
  const src = mode === 'edit' ? row : null;
  return {
    pay_name: src ? String(src.pay_name ?? '') : '',
    pay_cycle: src ? String(src.pay_cycle ?? '') : 'ROU',
    pay_day: src ? String(src.pay_day ?? '') : 'lmonth',
    pay_type: src ? String(src.pay_type ?? '') : 'BANK',
    amountDraft: src ? amountDraftOf(src.amount) : '',
    amountUsDraft: src ? amountDraftOf(src.amount_us) : '',
    // bank_code.code 는 숫자로 내려온다 - 은행 선택 목록의 code 와 같은 문자열로 맞춘다.
    bankcode: src && Number(src.bankcode ?? 0) > 0 ? String(Number(src.bankcode)) : '',
    banksn: src ? String(src.banksn ?? '') : '',
    bankinname: src ? String(src.bankinname ?? '') : '',
    cardcom: src ? String(src.cardcom ?? '') : '',
    use_area: src ? String(src.use_area ?? '0') : '0',
    up_name: src ? String(src.up_name ?? '') : defaultUpName,
    use_status: src ? String(src.use_status ?? '1') : '1',
    // 마지막 결제일은 그 건의 이력이라 수정에서만 다룬다 - 등록 폼에는 이 칸이 없다.
    last_pay_day: src ? String(src.last_pay_day ?? '').slice(0, 10) : '',
    memo: src ? String(src.memo ?? '') : '',
  };
}

/**
 * 정기결제 추가·수정 모달 — 입금건(BankPage 의 BankFormModal)·카드건(CardPage 의
 * CardFormModal)과 같은 구조·같은 UX다. 은행 선택은 입금건 관리의 드롭다운
 * (components/BankSelect)과 목록 API(/payment/bank/codes)를 그대로 쓴다.
 *
 * 다른 점은 컬럼뿐이다: bankcard_manage_list 의 결제주기(pay_cycle)·결제일
 * (pay_day)·결제방식(pay_type)·카드사(cardcom)·사용 여부(use_status)를 받고,
 * 마지막 결제일(last_pay_day)은 그 건의 이력이라 수정에서만 보인다.
 */
function RecurringFormModal({
  mode,
  initial,
  corp,
  corpOptions,
  defaultUpName,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  initial: Row | null;
  /** 목록에서 고른 법인 - 등록은 이 법인으로 저장한다(입금건·카드건과 같다). */
  corp: string;
  corpOptions: { value: string; label: string }[];
  defaultUpName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<RecurringFormState>(() =>
    toRecurringFormState(initial, defaultUpName, mode),
  );

  const codes = useQuery({
    queryKey: ['payment-bank', 'codes'],
    queryFn: () => paymentBankApi.codes(),
    staleTime: 10 * 60 * 1000,
  });

  const isEdit = mode === 'edit';
  const seq = isEdit && initial ? Number(initial.seq) : 0;
  /** 법인 코드를 목록 필터와 같은 이름으로. 목록에 없는 코드면 코드 그대로 보여준다. */
  const corpLabel = (code: string) => corpOptions.find((o) => o.value === code)?.label ?? code;

  const body: RecurringEditBody = {
    pay_name: form.pay_name,
    pay_cycle: form.pay_cycle,
    pay_day: form.pay_day,
    pay_type: form.pay_type,
    amount: form.amountDraft,
    amount_us: form.amountUsDraft,
    bankcode: form.bankcode,
    banksn: form.banksn,
    bankinname: form.bankinname,
    cardcom: form.cardcom,
    use_area: Number(form.use_area),
    up_name: form.up_name,
    use_status: Number(form.use_status),
    memo: form.memo,
  };

  const save = useMutation({
    mutationFn: () =>
      isEdit
        ? paymentRecurringApi.update(seq, { ...body, last_pay_day: form.last_pay_day })
        : paymentRecurringApi.create({ ...body, corp }),
    onSuccess: onSaved,
  });

  // 입금건·카드건과 같은 수준: 결제 항목·담당자·금액이 필수다(결제주기·결제일·
  // 결제방식은 기본값이 늘 채워져 있다).
  const valid = form.pay_name && form.up_name && form.amountDraft;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title">
          <span style={{ color: 'var(--accent)' }}>🔁</span>
          {isEdit ? '정기결제 수정' : '정기결제 추가'}
        </h2>

        {/* 어느 법인으로 저장되는지 폼 맨 위에서 먼저 보여준다 (카드건과 같다). */}
        <div className="form-summary">
          <div className="form-summary__corp">관리 법인: {corpLabel(String(initial?.corp ?? corp))}</div>
          <div className="form-summary__sub">
            {isEdit && initial
              ? `접수번호 #${String(initial.seq ?? '')} · 등록일: ${dateOnly(initial.regdate as string)}`
              : '목록에서 고른 법인으로 등록됩니다.'}
          </div>
        </div>

        <form
          // .form-grid(auto-fit 다열 그리드) 대신 .form-stack — 입금건 추가 폼과
          // 같은 이유다(BankPage.tsx 의 주석 참고).
          className="form-stack"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Field label="결제 항목">
            <input
              className="input"
              value={form.pay_name}
              onChange={(e) => setForm((f) => ({ ...f, pay_name: e.target.value }))}
            />
          </Field>

          <div className="row-2">
            <Field label="결제주기">
              <select
                className="input"
                value={form.pay_cycle}
                onChange={(e) => setForm((f) => ({ ...f, pay_cycle: e.target.value }))}
              >
                {Object.entries(PAY_CYCLES).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="결제일">
              <select
                className="input"
                value={form.pay_day}
                onChange={(e) => setForm((f) => ({ ...f, pay_day: e.target.value }))}
              >
                {PAY_DAY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="row-2">
            <Field label="담당부서">
              <select
                className="input"
                value={form.use_area}
                onChange={(e) => setForm((f) => ({ ...f, use_area: e.target.value }))}
              >
                {Object.entries(USE_AREAS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="담당자">
              <input
                className="input"
                value={form.up_name}
                onChange={(e) => setForm((f) => ({ ...f, up_name: e.target.value }))}
              />
            </Field>
          </div>

          <div className="row-2">
            <Field label="결제금액">
              <input
                className="input input--amount"
                value={form.amountDraft}
                onChange={(e) => setForm((f) => ({ ...f, amountDraft: formatAmountDraft(e.target.value) }))}
              />
            </Field>
            <Field label="결제금액(달러)">
              <input
                className="input input--amount"
                value={form.amountUsDraft}
                onChange={(e) => setForm((f) => ({ ...f, amountUsDraft: formatAmountDraft(e.target.value) }))}
              />
            </Field>
          </div>

          <Field label="결제방식">
            <div className="radio-group">
              {Object.entries(PAY_TYPES).map(([value, label]) => (
                <label key={value} className="radio-label">
                  <input
                    type="radio"
                    name="pay_type"
                    checked={form.pay_type === value}
                    onChange={() => setForm((f) => ({ ...f, pay_type: value }))}
                  />
                  {label}
                </label>
              ))}
            </div>
          </Field>

          {/* 계좌이체 건만 쓰는 칸이다 - 카드 건은 비워 두면 기존 데이터처럼 0/빈 값이 된다. */}
          <div className="form-divider">계좌이체 정보 (해당 건만)</div>
          <div className="row-2">
            <Field label="은행 선택" as="div">
              <BankSelect
                value={form.bankcode}
                options={codes.data ?? []}
                onChange={(code) => setForm((f) => ({ ...f, bankcode: code }))}
              />
            </Field>
            <Field label="계좌번호">
              <input
                className="input"
                value={form.banksn}
                onChange={(e) => setForm((f) => ({ ...f, banksn: e.target.value }))}
              />
            </Field>
          </div>
          <Field label="예금주">
            <input
              className="input"
              value={form.bankinname}
              onChange={(e) => setForm((f) => ({ ...f, bankinname: e.target.value }))}
            />
          </Field>

          {/* 카드결제 건만 쓰는 칸이다. */}
          <div className="form-divider">카드결제 정보 (해당 건만)</div>
          <Field label="카드사 / 결제업체">
            <input
              className="input"
              value={form.cardcom}
              onChange={(e) => setForm((f) => ({ ...f, cardcom: e.target.value }))}
            />
          </Field>

          <Field label="사용 여부">
            <div className="radio-group">
              {Object.entries(USE_STATUSES).map(([value, label]) => (
                <label key={value} className="radio-label">
                  <input
                    type="radio"
                    name="use_status"
                    checked={form.use_status === value}
                    onChange={() => setForm((f) => ({ ...f, use_status: value }))}
                  />
                  {label}
                </label>
              ))}
            </div>
          </Field>

          {isEdit && (
            <>
              <div className="form-divider">마지막 결제</div>
              <Field label="마지막 결제일">
                <div className="status-editor__row">
                  <input
                    className="input"
                    type="date"
                    value={form.last_pay_day}
                    onChange={(e) => setForm((f) => ({ ...f, last_pay_day: e.target.value }))}
                  />
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => setForm((f) => ({ ...f, last_pay_day: localDate() }))}
                  >
                    오늘
                  </button>
                </div>
              </Field>
            </>
          )}

          <div className="form-divider">비고</div>
          <Field label="비고 (메모)">
            <textarea
              className="input input--area"
              value={form.memo}
              onChange={(e) => setForm((f) => ({ ...f, memo: e.target.value }))}
            />
          </Field>

          {save.isError && (
            <div className="qty-msg qty-msg--error">
              {save.error instanceof ApiError ? save.error.message : '저장에 실패했습니다.'}
            </div>
          )}

          <button type="submit" className="btn btn--primary btn--block" disabled={!valid || save.isPending}>
            {save.isPending ? '저장 중…' : '저장하기'}
          </button>
        </form>
      </div>
    </div>
  );
}

export function RecurringPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const update = useUrlUpdate(params, setParams);
  const [q, setQ] = useUrlSearch(params, update, 'q');
  const corp = params.get('corp') || 'bnncompany';
  const kind = params.get('kind') ?? '';
  const upName = params.get('upName') ?? '';
  const useStatus = params.get('useStatus') ?? '';
  const page = Number(params.get('page') || '1');
  const [formModal, setFormModal] = useState<{ mode: 'create' | 'edit'; row: Row | null } | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<number | null>(null);

  const meta = useQuery({
    queryKey: ['report', 'payment-recurring', 'meta'],
    queryFn: () => reportsApi.meta('payment-recurring'),
    staleTime: 5 * 60 * 1000,
  });
  const listParams = useMemo(
    () => ({ q, corp, kind, upName, useStatus, page, perPage: 100, sort: 'regdate', dir: 'DESC' }),
    [q, corp, kind, upName, useStatus, page],
  );
  const list = useQuery({
    queryKey: ['payment-recurring', 'list', listParams],
    queryFn: () => reportsApi.data('payment-recurring', listParams),
  });

  function refresh() {
    void qc.invalidateQueries({ queryKey: ['payment-recurring', 'list'] });
  }

  // 입금건 삭제와 같다 - 한 번 더 확인한 뒤에만 소프트 삭제(is_del=1)한다.
  const remove = useMutation({
    mutationFn: (seq: number) => paymentRecurringApi.remove(seq),
    onSuccess: () => {
      setConfirmingDelete(null);
      refresh();
    },
  });

  const filterOptions = (key: string) => meta.data?.filters.find((filter) => filter.key === key)?.options ?? [];
  // meta 가 오기 전에도 법인 이름이 보이게 기본값을 둔다 (카드건과 같다).
  const corpOptions = filterOptions('corp').length > 0
    ? filterOptions('corp')
    : [{ value: 'bnncompany', label: '비앤앤 컴퍼니' }];
  const managerOptions = filterOptions('upName');
  const statusOptions = filterOptions('useStatus');
  const rows: Row[] = list.data?.items ?? [];

  return (
    <section className="payment-recurring-page">
      <header className="page-head payment-recurring-head">
        <div>
          <h1 className="page-title">정기결제 관리 <span className="badge badge--danger">관리자 전용</span></h1>
          <p className="page-desc">정기·수시 결제 항목과 담당자, 마지막 결제 상태를 확인합니다.</p>
        </div>
        <div className="page-head__actions">
          <Link className="btn btn--ghost btn--sm" to="/reports/payment-bank">입금건 관리</Link>
          <Link className="btn btn--ghost btn--sm" to="/reports/payment-card">카드결제 관리</Link>
        </div>
      </header>

      <div className="filters payment-recurring-filters">
        <Field label="법인">
          <select className="input" value={corp} onChange={(event) => update({ corp: event.target.value, page: 1 })}>
            {corpOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </Field>
        <Field label="검색">
          <SearchInput value={q} onChange={setQ} placeholder="결제이름 / 금액 / 계좌번호 / 카드사 / 메모" />
        </Field>
        <Field label="담당자">
          <select className="input" value={upName} onChange={(event) => update({ upName: event.target.value, page: 1 })}>
            <option value="">전체 담당자</option>
            {managerOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </Field>
        <Field label="사용 여부">
          <select className="input" value={useStatus} onChange={(event) => update({ useStatus: event.target.value, page: 1 })}>
            <option value="">전체</option>
            {statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </Field>
        <button type="button" className="btn btn--ghost filters__reset"
          onClick={() => setParams(new URLSearchParams())}>초기화</button>
      </div>

      <div className="tab-group payment-recurring-tabs" aria-label="결제 구분">
        {KIND_TABS.map((tab) => (
          <button key={tab.value || 'all'} type="button"
            className={kind === tab.value ? 'tab-btn tab-btn--active' : 'tab-btn'}
            onClick={() => update({ kind: tab.value || undefined, page: 1 })}>{tab.label}</button>
        ))}
      </div>

      <div className="toolbar payment-recurring-toolbar">
        {/* 위치·크기·스타일 모두 입금건 관리의 '+ 입금건 추가' 버튼과 같다. */}
        <button
          type="button"
          className="btn btn--primary btn--sm"
          style={{ marginLeft: 'auto' }}
          onClick={() => setFormModal({ mode: 'create', row: null })}
        >
          + 정기결제 추가
        </button>
      </div>

      {meta.isError ? (
        <ErrorBox error={meta.error} onRetry={() => void meta.refetch()} />
      ) : list.isPending ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyBox>조건에 맞는 정기·수시 결제 항목이 없습니다.</EmptyBox>
      ) : (
        <RecurringTable
          data={list.data}
          rows={rows}
          page={page}
          onEdit={(row) => setFormModal({ mode: 'edit', row })}
          confirmingDelete={confirmingDelete}
          onAskDelete={setConfirmingDelete}
          onDelete={(seq) => remove.mutate(seq)}
          deleting={remove.isPending}
        />
      )}

      {list.data && list.data.totalPages > 1 && (
        <Pagination page={list.data.page} totalPages={list.data.totalPages} total={list.data.total}
          onChange={(next) => update({ page: next }, false)} />
      )}

      {formModal && (
        <RecurringFormModal
          mode={formModal.mode}
          initial={formModal.row}
          corp={corp}
          corpOptions={corpOptions}
          defaultUpName={user?.name ?? user?.userid ?? ''}
          onClose={() => setFormModal(null)}
          onSaved={() => {
            // 입금건·카드건과 같다 — 모달을 닫고 목록 쿼리만 무효화한다(전체 새로고침 없음).
            setFormModal(null);
            refresh();
          }}
        />
      )}
    </section>
  );
}

function RecurringTable({
  data,
  rows,
  page,
  onEdit,
  confirmingDelete,
  onAskDelete,
  onDelete,
  deleting,
}: {
  data: ReportData;
  rows: Row[];
  page: number;
  onEdit: (row: Row) => void;
  confirmingDelete: number | null;
  onAskDelete: (seq: number | null) => void;
  onDelete: (seq: number) => void;
  deleting: boolean;
}) {
  return (
    <div className="table-wrap payment-recurring-table-wrap">
      <table className="table payment-recurring-table">
        <thead><tr>
          <th>No</th><th>결제 항목</th><th>담당</th><th>결제 방식 / 정보</th>
          <th style={{ textAlign: 'right' }}>결제금액</th><th style={{ textAlign: 'center' }}>사용 여부</th>
          <th>마지막 결제일</th><th>메모</th><th>등록 / 수정</th>
          <th className="payment-recurring-table__manage-head">관리</th>
        </tr></thead>
        <tbody>
          {rows.map((row, index) => {
            const active = String(row.use_status ?? '') === '1';
            const bank = String(row.pay_type ?? '') === 'BANK';
            const cycle = String(row.pay_cycle ?? '') === 'ROU' ? '정기결제' : '수시결제';
            return (
              <tr key={String(row.seq ?? index)} className={!active ? 'payment-recurring-row--inactive' : undefined}>
                <td className="td-muted">{data.total - ((page - 1) * data.perPage + index)}</td>
                <td><div className="payment-recurring-name"><strong>{text(String(row.pay_name ?? ''))}</strong><span>{cycle} · {payDayLabel(row.pay_day)}</span></div></td>
                <td><div className="payment-recurring-owner"><strong>{text(String(row.up_name ?? ''))}</strong><span>{USE_AREAS[String(row.use_area ?? '')] ?? `부서코드 ${String(row.use_area ?? '-')}`}</span></div></td>
                <td>
                  <div className="payment-recurring-method">
                    <span className={bank ? 'badge badge--info' : 'badge'}>{bank ? '계좌이체' : '카드결제'}</span>
                    {bank ? (
                      <small>{text(String(row.banksn ?? ''))} · {text(String(row.bankinname ?? ''))}</small>
                    ) : <small>{text(String(row.cardcom ?? ''))}</small>}
                  </div>
                </td>
                <td className="payment-recurring-amount">
                  <strong>{money(row.amount as number)}<small>원</small></strong>
                  {Number(row.amount_us ?? 0) > 0 && <span>{money(row.amount_us as number)} USD</span>}
                </td>
                <td className="payment-recurring-status">
                  <span className={active ? 'badge badge--ok' : 'badge badge--danger'}>{active ? '사용' : '미사용'}</span>
                </td>
                <td>
                  {row.last_pay_day ? <span className="payment-recurring-lastpay">{dateOnly(String(row.last_pay_day))}</span>
                    : <span className="badge badge--warn">결제 이력 없음</span>}
                </td>
                <td><span className="payment-recurring-note" title={String(row.memo ?? '')}>{text(String(row.memo ?? ''))}</span></td>
                <td className="payment-recurring-dates">
                  <span>{datetime(String(row.regdate ?? ''))}</span>
                  {!isZeroDate(row.last_update) && <small>수정 {datetime(String(row.last_update))}</small>}
                </td>
                <td className="payment-recurring-table__manage-cell">
                  {/* 위치·크기·스타일 모두 입금건 관리의 '수정'·'삭제' 버튼과 같다. */}
                  <div className="payment-recurring-actions" aria-label="정기결제 관리">
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm payment-recurring-actions__button"
                      onClick={() => onEdit(row)}
                    >
                      수정
                    </button>
                    {confirmingDelete === Number(row.seq) ? (
                      <>
                        <button
                          type="button"
                          className="btn btn--sm payment-recurring-actions__button payment-recurring-actions__button--delete-confirm"
                          disabled={deleting}
                          onClick={() => onDelete(Number(row.seq))}
                        >
                          정말 삭제
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm payment-recurring-actions__button"
                          onClick={() => onAskDelete(null)}
                        >
                          취소
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm payment-recurring-actions__button payment-recurring-actions__button--delete"
                        onClick={() => onAskDelete(Number(row.seq))}
                      >
                        삭제
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
        {data.totals && <tfoot><tr className="row--total">
          <td colSpan={4} style={{ textAlign: 'right' }}>합계</td>
          <td className="payment-recurring-amount"><strong>{money(data.totals.amount)}<small>원</small></strong></td>
          <td colSpan={5} />
        </tr></tfoot>}
      </table>
    </div>
  );
}
